// src/pages/Dashboard/Upload.js

import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import "../../styles/Upload.css";
import "../../styles/Tables.css";

import {
  uploadFileApi,
  getClientName,
  manualEntryApi,
  fetchDefraOptionsApi,
  downloadBreakdownExcelApi,
  fetchClientDataApi,
  deleteEntryApi,
} from "../../lib/api";
import BarChartScopes from "../../components/charts/BarChartScopes";

function safeSlug(s = "") {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_-]/g, "");
}

function safeNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function isValidYYYYMMDD(s) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(s || "").trim());
}

const ALLOWED_UPLOAD_EXTS = [".pdf", ".csv", ".xlsx", ".xls", ".ods"];
const MAX_UPLOAD_MB_UI = Number(process.env.REACT_APP_MAX_UPLOAD_MB || 25);

function getFileExtension(name = "") {
  const n = String(name || "").toLowerCase();
  const idx = n.lastIndexOf(".");
  return idx >= 0 ? n.slice(idx) : "";
}

function validateUploadFile(file) {
  if (!file) {
    return "Please select a file first.";
  }

  const ext = getFileExtension(file.name);
  if (!ALLOWED_UPLOAD_EXTS.includes(ext)) {
    return `Unsupported file type (${ext || "none"}). Allowed: ${ALLOWED_UPLOAD_EXTS.join(", ")}`;
  }

  const maxBytes = MAX_UPLOAD_MB_UI * 1024 * 1024;
  if (Number.isFinite(maxBytes) && file.size > maxBytes) {
    return `File too large (${(file.size / (1024 * 1024)).toFixed(1)}MB). Max allowed is ${MAX_UPLOAD_MB_UI}MB.`;
  }

  return null;
}

export default function Upload() {
  const navigate = useNavigate();
  const clientName = getClientName();

  const [dashboardData, setDashboardData] = useState(null);
  const [loadingCurrentData, setLoadingCurrentData] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [deletingGroupKey, setDeletingGroupKey] = useState("");
  const [scopeFilter, setScopeFilter] = useState("all");
  const [expandedGroups, setExpandedGroups] = useState({});

  const reloadCurrentData = useCallback(async () => {
    const dataRes = await fetchClientDataApi();
    setDashboardData(dataRes);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoadingCurrentData(true);
        await reloadCurrentData();
      } catch (err) {
        toast.error(err?.message || "Failed to load uploaded data");
      } finally {
        setLoadingCurrentData(false);
      }
    })();
  }, [reloadCurrentData]);

  const items = useMemo(() => dashboardData?.data?.parsed_items || [], [dashboardData]);

  const summary = useMemo(() => {
    const s = dashboardData?.data?.emissions_summary;
    const backendSummary = {
      scope_1_kgco2e: safeNumber(s?.scope_1_kgco2e),
      scope_2_kgco2e: safeNumber(s?.scope_2_kgco2e),
      scope_3_kgco2e: safeNumber(s?.scope_3_kgco2e),
      total_kgco2e: safeNumber(s?.total_kgco2e),
    };

    if (
      backendSummary.scope_1_kgco2e > 0 ||
      backendSummary.scope_2_kgco2e > 0 ||
      backendSummary.scope_3_kgco2e > 0 ||
      backendSummary.total_kgco2e > 0
    ) {
      return backendSummary;
    }

    return items.reduce(
      (acc, item) => {
        const value = safeNumber(item?.co2e_kg);
        const scope = String(item?.scope || "").trim();
        if (scope === "Scope 1") acc.scope_1_kgco2e += value;
        else if (scope === "Scope 2") acc.scope_2_kgco2e += value;
        else if (scope === "Scope 3") acc.scope_3_kgco2e += value;
        acc.total_kgco2e += value;
        return acc;
      },
      { scope_1_kgco2e: 0, scope_2_kgco2e: 0, scope_3_kgco2e: 0, total_kgco2e: 0 }
    );
  }, [dashboardData, items]);

  const filteredItems = useMemo(() => {
    if (scopeFilter === "all") return items;
    return items.filter((item) => item?.scope === scopeFilter);
  }, [items, scopeFilter]);

  const groupedItems = useMemo(() => {
    const grouped = new Map();

    for (const item of filteredItems) {
      const source = String(item?.source || item?.filename || "upload").trim() || "upload";
      const uploadedAt = String(item?.uploaded_at || "").trim();
      const activityDate = String(item?.activity_date || item?.date || "").trim();
      const groupKey = `${source}__${uploadedAt}__${activityDate}`;

      if (!grouped.has(groupKey)) {
        grouped.set(groupKey, {
          key: groupKey,
          source,
          uploadedAt,
          activityDate,
          count: 0,
          totalKgCo2e: 0,
          items: [],
        });
      }

      const bucket = grouped.get(groupKey);
      bucket.count += 1;
      bucket.totalKgCo2e += safeNumber(item?.co2e_kg);
      bucket.items.push(item);
    }

    return Array.from(grouped.values()).sort((a, b) => {
      const aTime = new Date(a.activityDate || a.uploadedAt || 0).getTime();
      const bTime = new Date(b.activityDate || b.uploadedAt || 0).getTime();
      return bTime - aTime;
    });
  }, [filteredItems]);

  const scopeCounts = useMemo(() => {
    return {
      s1: items.filter((i) => i.scope === "Scope 1").length,
      s2: items.filter((i) => i.scope === "Scope 2").length,
      s3: items.filter((i) => i.scope === "Scope 3").length,
    };
  }, [items]);

  useEffect(() => {
    setExpandedGroups((prev) => {
      const next = {};
      for (const group of groupedItems) {
        next[group.key] = Object.prototype.hasOwnProperty.call(prev, group.key) ? prev[group.key] : false;
      }
      return next;
    });
  }, [groupedItems]);

  const toggleGroupExpanded = (groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const handleRemoveRow = async (item) => {
    const entryId = item?.id;

    if (!entryId) {
      toast.error("This entry has no ID yet. The backend must assign IDs before it can be removed.");
      return;
    }

    const ok = window.confirm("Remove this entry? This cannot be undone.");
    if (!ok) return;

    try {
      setDeletingId(entryId);
      toast.loading("Removing entry...", { id: "rm-upload" });
      await deleteEntryApi(entryId);
      toast.success("Entry removed", { id: "rm-upload" });
      await reloadCurrentData();
    } catch (err) {
      toast.error(err?.message || "Failed to remove entry", { id: "rm-upload" });
    } finally {
      setDeletingId(null);
    }
  };

  const handleRemoveGroup = async (group) => {
    const entryIds = (group?.items || []).map((item) => item?.id).filter(Boolean);
    if (!entryIds.length) {
      toast.error("This upload group has no removable entry IDs.");
      return;
    }

    const ok = window.confirm(
      `Remove ${entryIds.length} entr${entryIds.length === 1 ? "y" : "ies"} from ${group?.source || "this upload"}? This cannot be undone.`
    );
    if (!ok) return;

    try {
      setDeletingGroupKey(group.key);
      toast.loading("Removing upload group...", { id: "rm-group" });
      for (const entryId of entryIds) {
        await deleteEntryApi(entryId);
      }
      toast.success("Upload group removed", { id: "rm-group" });
      await reloadCurrentData();
    } catch (err) {
      toast.error(err?.message || "Failed to remove upload group", { id: "rm-group" });
    } finally {
      setDeletingGroupKey("");
    }
  };

  // ----- Upload file -----
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("");
  const [lastUploadSummary, setLastUploadSummary] = useState(null);
  const [uploading, setUploading] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const [uploadActivityDate, setUploadActivityDate] = useState(today);
  const uploadFileValidationError = useMemo(() => validateUploadFile(file), [file]);
  const uploadReady = Boolean(file) && !uploading && !uploadFileValidationError;

  const handleFileSubmit = async (e) => {
    e.preventDefault();

    const fileValidationError = uploadFileValidationError;
    if (fileValidationError) {
      setStatus(fileValidationError);
      toast.error(fileValidationError);
      return;
    }

    if (uploadActivityDate && !isValidYYYYMMDD(uploadActivityDate)) {
      toast.error("Activity date must be YYYY-MM-DD");
      return;
    }

    try {
      setUploading(true);
      setStatus("Uploading...");
      toast.loading("Uploading and processing...", { id: "upload" });

      const data = await uploadFileApi(file, uploadActivityDate);
      const uploadResult = data?.upload_result || {};
      const total = uploadResult?.emissions_summary?.total_kgco2e ?? 0;
      const parsedCount = Number.isFinite(Number(uploadResult?.parsed_count))
        ? Number(uploadResult.parsed_count)
        : 0;

      toast.success("Upload complete", { id: "upload" });
      setStatus(`Upload complete. Total emissions from this upload: ${Number(total).toLocaleString()} kg CO2e`);
      setLastUploadSummary({
        filename: file?.name || "",
        totalKgCo2e: Number(total) || 0,
        parsedCount,
      });

      setFile(null);
      await reloadCurrentData();
    } catch (err) {
      toast.error(err?.message || "Upload failed", { id: "upload" });
      setStatus("Upload failed: " + (err?.message || "Unknown error"));
    } finally {
      setUploading(false);
    }
  };

  // ----- Download breakdown Excel -----
  const [downloadingBreakdown, setDownloadingBreakdown] = useState(false);

  const handleDownloadBreakdown = async () => {
    try {
      setDownloadingBreakdown(true);
      toast.loading("Preparing spreadsheet...", { id: "xl" });

      const blob = await downloadBreakdownExcelApi();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      const clientSlug = safeSlug(clientName || "");
      const date = new Date().toISOString().slice(0, 10);
      a.download = clientSlug
        ? `${clientSlug}_emissions_breakdown_${date}.xlsx`
        : `emissions_breakdown_${date}.xlsx`;

      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);

      toast.success("Spreadsheet downloaded", { id: "xl" });
    } catch (err) {
      toast.error(err?.message || "Failed to download spreadsheet", { id: "xl" });
    } finally {
      setDownloadingBreakdown(false);
    }
  };

  // ----- Manual entry -----
  const [savingManual, setSavingManual] = useState(false);
  const [manualDate, setManualDate] = useState(today);
  const [manualScope, setManualScope] = useState("Scope 2");
  const [defraOptions, setDefraOptions] = useState(null);
  const [loadingDefra, setLoadingDefra] = useState(true);
  const [defraStatus, setDefraStatus] = useState({ type: "idle", message: "" });
  const [defraReloadNonce, setDefraReloadNonce] = useState(0);
  const [manualCategory, setManualCategory] = useState("");
  const [manualUnit, setManualUnit] = useState("");
  const [manualQty, setManualQty] = useState("");
  const [manualDesc, setManualDesc] = useState("");
  const [categoryQuery, setCategoryQuery] = useState("");
  const [level1, setLevel1] = useState("");
  const [level2, setLevel2] = useState("");
  const [level3, setLevel3] = useState("");

  useEffect(() => {
    (async () => {
      try {
        setLoadingDefra(true);
        setDefraStatus({ type: "idle", message: "" });
        const res = await fetchDefraOptionsApi(manualScope);
        setDefraOptions(res);

        setCategoryQuery("");
        setLevel1("");
        setLevel2("");
        setLevel3("");

        const cats = res?.categories || [];
        const unitsMap = res?.unitsByCategory || {};

        if (cats.length) {
          setDefraStatus({ type: "ready", message: "" });
          const firstCat = cats[0];
          setManualCategory(firstCat);
          const units = unitsMap[firstCat] || [];
          setManualUnit(units[0] || "");
        } else {
          setDefraStatus({
            type: "empty",
            message:
              "The server responded, but it did not return any DEFRA categories. This usually means the DEFRA workbook is missing on the server or could not be parsed.",
          });
          setManualCategory("");
          setManualUnit("");
        }
      } catch (err) {
        const message = err?.message || "Failed to load DEFRA dropdown options";
        toast.error(message);
        setDefraStatus({
          type: "error",
          message:
            "The platform could not fetch DEFRA dropdown options from the backend. Check that the backend service is running and that /defra/options is reachable.",
        });
        setDefraOptions(null);
        setManualCategory("");
        setManualUnit("");
      } finally {
        setLoadingDefra(false);
      }
    })();
  }, [manualScope, defraReloadNonce]);

  const defraLooksValid = useMemo(() => {
    return Array.isArray(defraOptions?.categories) && defraOptions.categories.length > 0;
  }, [defraOptions]);

  const parsedCategories = useMemo(() => {
    const cats = defraOptions?.categories || [];
    return cats.map((label) => {
      const parts = String(label)
        .split(" - ")
        .map((s) => s.trim())
        .filter(Boolean);
      return { label, parts };
    });
  }, [defraOptions]);

  const level1Options = useMemo(() => {
    const set = new Set();
    for (const c of parsedCategories) {
      if (c.parts[0]) set.add(c.parts[0]);
    }
    return Array.from(set).sort();
  }, [parsedCategories]);

  const level2Options = useMemo(() => {
    if (!level1) return [];
    const set = new Set();
    for (const c of parsedCategories) {
      if (c.parts[0] === level1 && c.parts[1]) set.add(c.parts[1]);
    }
    return Array.from(set).sort();
  }, [parsedCategories, level1]);

  const level3Options = useMemo(() => {
    if (!level1 || !level2) return [];
    const set = new Set();
    for (const c of parsedCategories) {
      if (c.parts[0] === level1 && c.parts[1] === level2 && c.parts[2]) set.add(c.parts[2]);
    }
    return Array.from(set).sort();
  }, [parsedCategories, level1, level2]);

  useEffect(() => {
    setLevel2("");
    setLevel3("");
  }, [level1]);

  useEffect(() => {
    setLevel3("");
  }, [level2]);

  const filteredCategories = useMemo(() => {
    let list = parsedCategories;

    if (level1) list = list.filter((c) => c.parts[0] === level1);
    if (level2) list = list.filter((c) => c.parts[1] === level2);
    if (level3) list = list.filter((c) => c.parts[2] === level3);

    const q = (categoryQuery || "").trim().toLowerCase();
    if (q) list = list.filter((c) => c.label.toLowerCase().includes(q));

    return list.map((c) => c.label);
  }, [parsedCategories, level1, level2, level3, categoryQuery]);

  useEffect(() => {
    if (!defraLooksValid) return;

    if (!filteredCategories.includes(manualCategory)) {
      const next = filteredCategories[0] || "";
      setManualCategory(next);
      const units = defraOptions?.unitsByCategory?.[next] || [];
      setManualUnit(units[0] || "");
    }
  }, [filteredCategories, manualCategory, defraLooksValid, defraOptions]);

  const unitsForSelectedCategory = useMemo(() => {
    if (!defraOptions?.unitsByCategory || !manualCategory) return [];
    return defraOptions.unitsByCategory[manualCategory] || [];
  }, [defraOptions, manualCategory]);

  useEffect(() => {
    if (!unitsForSelectedCategory.length) {
      setManualUnit("");
      return;
    }
    if (!unitsForSelectedCategory.includes(manualUnit)) {
      setManualUnit(unitsForSelectedCategory[0]);
    }
  }, [unitsForSelectedCategory, manualUnit]);

  const resetDefraFilters = () => {
    setCategoryQuery("");
    setLevel1("");
    setLevel2("");
    setLevel3("");

    const cats = defraOptions?.categories || [];
    const unitsMap = defraOptions?.unitsByCategory || {};
    const firstCat = cats[0] || "";

    setManualCategory(firstCat);

    const units = unitsMap[firstCat] || [];
    setManualUnit(units[0] || "");
  };

  const hasActiveFilters = useMemo(() => {
    const q = (categoryQuery || "").trim();
    return Boolean(level1 || level2 || level3 || q);
  }, [level1, level2, level3, categoryQuery]);

  const retryDefraLoad = () => {
    setDefraReloadNonce((v) => v + 1);
  };

  const resetUploadSelection = () => {
    setFile(null);
    setStatus("");
    setLastUploadSummary(null);
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();

    if (!manualDate || !isValidYYYYMMDD(manualDate)) return toast.error("Please choose a valid date");
    if (!manualScope) return toast.error("Please choose a scope");
    if (!defraLooksValid) {
      return toast.error(
        defraStatus?.type === "empty"
          ? "DEFRA options are loaded, but there are no categories available."
          : "DEFRA categories are not loading yet."
      );
    }
    if (!manualCategory) return toast.error("Please choose a DEFRA category");
    if (!manualUnit) return toast.error("Please choose a unit");
    if (manualQty === "" || Number.isNaN(Number(manualQty))) return toast.error("Please enter a valid quantity");

    try {
      setSavingManual(true);
      toast.loading("Saving manual entry...", { id: "manual" });

      const res = await manualEntryApi({
        date: manualDate,
        scope: manualScope,
        category: manualCategory,
        quantity: Number(manualQty),
        unit: manualUnit,
        description: manualDesc,
      });

      const co2e = Number(res?.item?.co2e_kg ?? res?.co2e_kg ?? 0) || 0;
      toast.success(`Saved - ${co2e.toLocaleString()} kg CO2e`, { id: "manual" });

      setManualQty("");
      setManualDesc("");
      await reloadCurrentData();
    } catch (err) {
      toast.error(err?.message || "Manual entry failed", { id: "manual" });
    } finally {
      setSavingManual(false);
    }
  };

  return (
    <div className="upload-page">
      <h2>Upload data for {clientName || "your organisation"}</h2>

      <div className="upload-box">
        <h3 style={{ marginTop: 0 }}>Upload documents</h3>
        <p style={{ marginTop: 6, opacity: 0.8 }}>
          Upload PDF, CSV, Excel or OpenDocument spreadsheet files. We'll extract activity data and calculate emissions.
        </p>
        <p style={{ marginTop: 4, opacity: 0.75, fontSize: 12 }}>
          Accepted: {ALLOWED_UPLOAD_EXTS.join(", ")} | Max file size: {MAX_UPLOAD_MB_UI}MB
        </p>
        <div className="upload-help-panel">
          <strong>Best results</strong>
          <ul>
            <li>Use bills, invoices, meter exports, fuel logs, travel records or waste summaries.</li>
            <li>For shared team spreadsheets, <code>.xlsx</code> and <code>.ods</code> are both supported.</li>
            <li>If nothing is extracted, the file may be a template, image-only scan or summary sheet without quantities.</li>
          </ul>
        </div>

        <div style={{ marginTop: 12, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 12, fontWeight: 700, opacity: 0.8 }}>Activity date (for charts)</label>
            <input
              type="date"
              value={uploadActivityDate}
              onChange={(e) => setUploadActivityDate(e.target.value)}
              style={{ maxWidth: 220 }}
              disabled={uploading}
            />
          </div>

          <div style={{ fontSize: 12, opacity: 0.75, maxWidth: 420 }}>
            This sets the date your file will appear on the trend charts (use the invoice or bill period date).
          </div>
        </div>

        <form onSubmit={handleFileSubmit} style={{ marginTop: 12 }}>
          <input
            type="file"
            accept=".pdf,.csv,.xlsx,.xls,.ods"
            onChange={(e) => {
              const picked = e.target.files?.[0] || null;
              setFile(picked);
              setLastUploadSummary(null);
              if (!picked) {
                setStatus("");
                return;
              }
              const err = validateUploadFile(picked);
              if (err) {
                setStatus(err);
                toast.error(err);
              } else {
                setStatus(`Ready to upload: ${picked.name}`);
              }
            }}
            disabled={uploading}
          />

          {file ? (
            <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
              Selected: <b>{file.name}</b>
            </div>
          ) : null}

          <button
            type="submit"
            disabled={!file || uploading || Boolean(uploadFileValidationError)}
            className={`upload-submit-btn${uploadReady ? " is-ready" : ""}`}
            style={{ marginTop: 10 }}
          >
            {uploading ? "Uploading..." : "Upload"}
          </button>
        </form>

        {lastUploadSummary ? (
          <div className="upload-success-panel">
            <h4>Upload processed</h4>
            <p>
              <strong>{lastUploadSummary.filename}</strong> is ready. This upload produced {lastUploadSummary.parsedCount} item(s)
              with a total of {lastUploadSummary.totalKgCo2e.toLocaleString()} kg CO2e.
            </p>
            <div className="upload-success-actions">
              <button type="button" className="btn-primary" onClick={() => navigate("/dashboard")}>
                View dashboard
              </button>
              <button type="button" className="btn-secondary" onClick={resetUploadSelection}>
                Upload another file
              </button>
            </div>
          </div>
        ) : null}

        <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            className="btn-secondary"
            onClick={handleDownloadBreakdown}
            disabled={downloadingBreakdown}
          >
            {downloadingBreakdown ? "Preparing..." : "Download calculation breakdown (Excel)"}
          </button>

          <button type="button" className="btn-secondary" onClick={() => navigate("/dashboard")}>
            Back to dashboard
          </button>
        </div>

        {status && (
          <p
            className={`upload-message${
              /^upload failed/i.test(status)
                ? " is-error"
                : /^ready to upload/i.test(status)
                  ? " is-ready"
                  : ""
            }`}
          >
            {status}
          </p>
        )}
      </div>

      <div className="upload-box" style={{ marginTop: 18 }}>
        <h3 style={{ marginTop: 0 }}>Manual data entry</h3>
        <p style={{ marginTop: 6, opacity: 0.8 }}>
          Add entries directly into the platform. DEFRA factors are applied automatically via dropdown selection.
        </p>

        {loadingDefra ? (
          <p style={{ opacity: 0.8 }}>Loading DEFRA categories...</p>
        ) : !defraLooksValid ? (
          <div style={{ marginTop: 10, padding: 12, border: "1px solid #f0c36d", borderRadius: 8 }}>
            <b>DEFRA dropdowns aren't ready yet.</b>
            <p style={{ marginTop: 6, opacity: 0.85 }}>
              {defraStatus?.message ||
                "Your backend /defra/options is not returning categories/units correctly."}
            </p>
            <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button type="button" className="btn-secondary" onClick={retryDefraLoad}>
                Retry DEFRA load
              </button>
              <span style={{ fontSize: 12, opacity: 0.75, alignSelf: "center" }}>
                Expected backend route: <code>/defra/options</code>
              </span>
            </div>
          </div>
        ) : (
          <form onSubmit={handleManualSubmit} className="manual-form">
            <div className="manual-grid">
              <div className="manual-field">
                <label>Date</label>
                <input type="date" value={manualDate} onChange={(e) => setManualDate(e.target.value)} />
              </div>

              <div className="manual-field">
                <label>Scope</label>
                <select value={manualScope} onChange={(e) => setManualScope(e.target.value)}>
                  <option value="Scope 1">Scope 1</option>
                  <option value="Scope 2">Scope 2</option>
                  <option value="Scope 3">Scope 3</option>
                </select>
              </div>

              <div className="manual-field">
                <label>Group</label>
                <select value={level1} onChange={(e) => setLevel1(e.target.value)}>
                  <option value="">All</option>
                  {level1Options.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>

              <div className="manual-field">
                <label>Subgroup</label>
                <select
                  value={level2}
                  onChange={(e) => setLevel2(e.target.value)}
                  disabled={!level1Options.length || !level1}
                >
                  <option value="">All</option>
                  {level2Options.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>

              <div className="manual-field">
                <label>Type</label>
                <select value={level3} onChange={(e) => setLevel3(e.target.value)} disabled={!level2}>
                  <option value="">All</option>
                  {level3Options.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>

              <div className="manual-field">
                <label>Category (DEFRA)</label>
                <select value={manualCategory} onChange={(e) => setManualCategory(e.target.value)}>
                  {filteredCategories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>

                <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8 }}>
                  <input
                    type="text"
                    value={categoryQuery}
                    onChange={(e) => setCategoryQuery(e.target.value)}
                    placeholder="Search category..."
                    style={{ flex: 1 }}
                  />

                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={resetDefraFilters}
                    disabled={!hasActiveFilters}
                    title="Clear filters and reset DEFRA selection"
                  >
                    Clear filters
                  </button>
                </div>

                {(level1 || level2 || level3 || categoryQuery.trim()) && (
                  <div style={{ marginTop: 6, fontSize: 12, opacity: 0.75 }}>
                    Showing {filteredCategories.length} match{filteredCategories.length === 1 ? "" : "es"}
                  </div>
                )}
              </div>

              <div className="manual-field">
                <label>Unit</label>
                <select
                  value={manualUnit}
                  onChange={(e) => setManualUnit(e.target.value)}
                  disabled={!unitsForSelectedCategory.length}
                >
                  {unitsForSelectedCategory.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>

              <div className="manual-field">
                <label>Quantity</label>
                <input
                  type="number"
                  value={manualQty}
                  onChange={(e) => setManualQty(e.target.value)}
                  placeholder="e.g. 1200"
                  min="0"
                  step="any"
                />
              </div>

              <div className="manual-field manual-wide">
                <label>Description (optional)</label>
                <input
                  type="text"
                  value={manualDesc}
                  onChange={(e) => setManualDesc(e.target.value)}
                  placeholder="Optional note"
                />
              </div>
            </div>

            <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button type="submit" disabled={savingManual}>
                {savingManual ? "Saving..." : "Save manual entry"}
              </button>
              <button type="button" className="btn-secondary" onClick={() => navigate("/dashboard")}>
                Back to dashboard
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="upload-box" style={{ marginTop: 18 }}>
        <div className="upload-section-header">
          <div>
            <h3 style={{ marginTop: 0 }}>Current uploaded data</h3>
            <p style={{ marginTop: 6, marginBottom: 0, opacity: 0.8 }}>
              Review the current scope totals and manage uploaded data in one place.
            </p>
          </div>
          <span className="upload-stat-pill">
            Total: {safeNumber(summary.total_kgco2e).toLocaleString()} kg CO2e
          </span>
        </div>

        {loadingCurrentData ? (
          <p style={{ marginTop: 14, opacity: 0.8 }}>Loading current uploaded data...</p>
        ) : (
          <div className="table-card upload-chart-card" style={{ marginTop: 16 }}>
            <div className="table-header">
              <h2>Scope breakdown</h2>
            </div>
            <BarChartScopes summary={summary} compact />
          </div>
        )}
      </div>

      <section className="table-section upload-page-table-section">
        <div className="table-header">
          <h2>Uploaded and parsed data</h2>
          <span className="pill pill-soft">
            Scope 1: {scopeCounts.s1} | Scope 2: {scopeCounts.s2} | Scope 3: {scopeCounts.s3}
          </span>
        </div>

        <div className="upload-filter-row">
          <button
            className={`btn-secondary ${scopeFilter === "all" ? "active" : ""}`}
            type="button"
            onClick={() => setScopeFilter("all")}
          >
            All
          </button>
          <button
            className={`btn-secondary ${scopeFilter === "Scope 1" ? "active" : ""}`}
            type="button"
            onClick={() => setScopeFilter("Scope 1")}
          >
            Scope 1
          </button>
          <button
            className={`btn-secondary ${scopeFilter === "Scope 2" ? "active" : ""}`}
            type="button"
            onClick={() => setScopeFilter("Scope 2")}
          >
            Scope 2
          </button>
          <button
            className={`btn-secondary ${scopeFilter === "Scope 3" ? "active" : ""}`}
            type="button"
            onClick={() => setScopeFilter("Scope 3")}
          >
            Scope 3
          </button>

          <span className="upload-filter-count">
            Showing {filteredItems.length} {filteredItems.length === 1 ? "entry" : "entries"} across {groupedItems.length} {groupedItems.length === 1 ? "upload" : "uploads"}
          </span>
        </div>

        <div className="table-card">
          <div className="upload-scroll-region">
            <table>
              <thead>
                <tr>
                  <th>Activity date</th>
                  <th>Uploaded</th>
                  <th>Source</th>
                  <th>Scope</th>
                  <th>Value</th>
                  <th>Unit</th>
                  <th>CO2e (kg)</th>
                  <th style={{ width: 140 }}></th>
                </tr>
              </thead>
              <tbody>
                {groupedItems.map((group) => {
                  const groupDeleting = deletingGroupKey === group.key;
                  const isExpanded = Boolean(expandedGroups[group.key]);

                  return (
                    <React.Fragment key={group.key}>
                      <tr className="upload-group-row">
                        <td colSpan={8}>
                          <div className="upload-group-summary">
                            <div className="upload-group-summary-main">
                              <button
                                className="upload-group-toggle"
                                type="button"
                                onClick={() => toggleGroupExpanded(group.key)}
                                aria-expanded={isExpanded}
                                aria-label={`${isExpanded ? "Collapse" : "Expand"} ${group.source || "upload"} entries`}
                              >
                                <span className={`upload-group-caret${isExpanded ? " is-open" : ""}`}>{">"}</span>
                                <span className="upload-group-toggle-label">
                                  {isExpanded ? "Hide parsed entries" : "Show parsed entries"}
                                </span>
                              </button>

                              <div className="upload-group-summary-text">
                                <strong>{group.source || "Upload"}</strong>
                                <span>Activity date: {group.activityDate || "-"}</span>
                                <span>Uploaded: {group.uploadedAt ? new Date(group.uploadedAt).toLocaleString() : "-"}</span>
                                <span>{group.count} {group.count === 1 ? "entry" : "entries"}</span>
                                <span>Total: {safeNumber(group.totalKgCo2e).toLocaleString()} kg CO2e</span>
                              </div>
                            </div>
                            <button
                              className="btn-secondary"
                              type="button"
                              onClick={() => handleRemoveGroup(group)}
                              disabled={groupDeleting}
                            >
                              {groupDeleting ? "Removing upload..." : "Remove upload"}
                            </button>
                          </div>
                        </td>
                      </tr>

                      {isExpanded && group.items.map((item, idx) => {
                        const rowKey = item?.id || `${item?.uploaded_at || "na"}-${idx}`;
                        const isDeleting = deletingId && item?.id === deletingId;

                        return (
                          <tr key={rowKey}>
                            <td>{item?.date || item?.activity_date || "-"}</td>
                            <td>{item?.uploaded_at ? new Date(item.uploaded_at).toLocaleDateString() : "-"}</td>
                            <td>{item?.source || "-"}</td>
                            <td>{item?.scope || "-"}</td>
                            <td>{item?.value ?? "-"}</td>
                            <td>{item?.unit || "-"}</td>
                            <td>{safeNumber(item?.co2e_kg).toLocaleString()}</td>
                            <td style={{ textAlign: "right" }}>
                              <button
                                className="btn-secondary"
                                type="button"
                                onClick={() => handleRemoveRow(item)}
                                disabled={!item?.id || isDeleting || groupDeleting}
                                title={!item?.id ? "Missing entry id (backend must assign IDs)" : "Remove this entry"}
                              >
                                {isDeleting ? "Removing..." : "Remove"}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}

                {groupedItems.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ padding: 14, opacity: 0.8 }}>
                      No uploaded entries match this filter yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
